// native
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Observable, Subscription } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

// service
import { CollectionsService } from '../../core/services/collections.service';
import { ItemsDisplayService } from '../../core/services/items-display.service';
import { ActiveReferencesService } from '../../core/services/active-references.service';
import { CitationsEventService } from '../../core/services/citations-event.service';

// model
import { ICollection, IDisplayItem, ICitationSelection } from '../../models';

// constant
import { SEARCH_ELEMENT_HEIGHT } from '../../constants/constants';

@Component({
  selector: 'scs-references',
  templateUrl: './references.component.html',
  host: {
    'class': 'content'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReferencesComponent implements OnInit, OnDestroy {

  collections$: Observable<ICollection[]>;
  sharedCollections$: Observable<ICollection[]>;
  privateCollection$: Observable<ICollection>;

  citationSelectedSubscription: Subscription;
  itemAddedToCitationQueueSubscription: Subscription;
  clearCitationQueueSubscription: Subscription;
  citationsHeightSubscription: Subscription;

  hasActiveReferences: boolean = false;
  isCitationDrawerVisible: boolean = false;
  containerHeight: SafeStyle;

  constructor(
    public collectionsService: CollectionsService,
    public itemsDisplayService: ItemsDisplayService,
    public activeReferencesService: ActiveReferencesService,
    private citationsEventService: CitationsEventService,
    private changeDetectorRef: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.setCollectionObservables();

    this.initCitationSubscriptions();
  }

  private setCollectionObservables(): void {
    this.collections$ = this.collectionsService.getCollections().pipe(
      shareReplay({ refCount: true, bufferSize: 1 })
    );

    this.privateCollection$ = this.collections$.pipe(
      map((collections: ICollection[]) => {
        return collections.filter(collection => {
          return !collection.shared && collection.status === 'active';
        });
      }),
      map((collections: ICollection[]) => collections[0])
    );

    this.sharedCollections$ = this.collections$.pipe(
      map((collections: ICollection[]) => {
        return collections.filter(collection => {
          return collection.shared && collection.user?.role && collection.status === 'active';
        });
      }),
      map((collections: ICollection[]) => collections.sort((a, b) => {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      }))
    );
  }

  private initCitationSubscriptions(): void {
    this.clearCitationQueueSubscription = this.citationsEventService.clearCitationQueue$.subscribe(() => {
      if (!this.citationsEventService.isCitationSelected) {
        this.isCitationDrawerVisible = false;
      }
    });

    this.citationSelectedSubscription = this.citationsEventService.citationSelected$.subscribe((selection: ICitationSelection) => {
      if (selection.isCitationDeselected) {
        this.isCitationDrawerVisible = false;
      }
      if (!!selection?.items) {
        this.isCitationDrawerVisible = true;
      }
    });

    this.citationsHeightSubscription = this.citationsEventService.citationContainerOffsetHeight$.subscribe((height: number) => {
      this.containerHeight = this.sanitizer.bypassSecurityTrustStyle(`calc(100% - ${(height + SEARCH_ELEMENT_HEIGHT).toString()}px)`);
      // todo here mark for check is needed possibly because it is a binding of child component, please take a look
      this.changeDetectorRef.markForCheck();
    });

    this.itemAddedToCitationQueueSubscription = this.citationsEventService.itemAddedToCitationQueue$.subscribe((item: IDisplayItem) => {
      this.isCitationDrawerVisible = true;
    });
  }

  ngOnDestroy(): void {
    this.citationSelectedSubscription?.unsubscribe();
    this.itemAddedToCitationQueueSubscription?.unsubscribe();
    this.clearCitationQueueSubscription?.unsubscribe();
    this.citationsHeightSubscription?.unsubscribe();
  }

  collectionTrackByFn(index: number, collection: ICollection): string {
    return collection.id;
  }

  openActiveReferences(): void {
    this.activeReferencesService.areActiveReferencesShown = true;
  }
}
